import { StateCreator } from 'zustand';
import { householdService } from '../../services/householdService';
import { syncService } from '../../services/syncService';
import { membershipService } from '../../services/membershipService';
import {
  Chore,
  HouseholdAggregate,
  ChoreAggregate,
  HouseholdMember,
  OfflineQueueItem,
  Schedule,
  describeSyncFailures,
  isRefusedByServer,
  refusedMessage,
} from '../../domain';
import type { AppStore } from '../index';

export interface ChoresSlice {
  chores: {
    households: HouseholdAggregate[];
    loading: boolean;   // true only during the initial data fetch
    mutating: boolean;  // true while a write operation (add/update/delete) is in flight
    error: string | null;
    syncStatus: {
      isOnline: boolean;
      isSyncing: boolean;
      lastSyncTime: Date | null;
      pendingActions: OfflineQueueItem[];
      lastSyncError: string | null;
      failedActions: OfflineQueueItem[];
    };
    /** The most recent markChoreDone call, offered as an "Undo" toast for as long as its
     * ChoreCompleted action is still sitting unsynced in the offline queue. Only one at a time —
     * a newer completion simply replaces it, it doesn't queue up a stack of undos. */
    undoableCompletion: {
      householdId: string;
      choreId: string;
      choreName?: string;
      activityId: string;
      queueItemId: number;
    } | null;
    load: () => Promise<void>;
    reset: () => Promise<void>;
    /** Resolves to the chore that now exists, or null if it could not be created. A caller
     * deciding whether to retry must read this rather than `chores.error`: a chore can be created
     * and then fail to queue for sync, which sets an error but leaves a real chore behind —
     * retrying on that would create a second one (see googleTasksSlice). */
    addChore: (
      name: string,
      description: string,
      schedule: Schedule,
      householdId: string,
      assignments: { userId: string; isResponsible: boolean }[],
      labels?: string[]
    ) => Promise<Chore | null>;
    updateChore: (
      householdId: string,
      choreId: string,
      name: string,
      description: string,
      schedule: Schedule,
      assignments: { userId: string; isResponsible: boolean }[],
      labels?: string[]
    ) => Promise<void>;
    addHousehold: (name: string, ownerId: string, ownerDisplayName: string) => Promise<void>;
    deleteHousehold: (householdId: string) => Promise<void>;
    /** Takes someone out of a household, on the server first: it needs to be online, and rejects
     * with the server's reason, for the caller to show, when it can't be done. */
    removeMember: (householdId: string, memberId: string) => Promise<void>;
    /** Makes a member one of the household's children, or not (docs/plans/children.md), on the
     * server first like removeMember: it needs to be online, and rejects with the server's reason,
     * for the caller to show, when it can't be done. */
    setChild: (householdId: string, memberId: string, isChild: boolean) => Promise<void>;
    markChoreDone: (householdId: string, choreId: string, userId: string, completedAt?: string) => Promise<void>;
    /** Reverts the tracked undoableCompletion, but only if it hasn't synced yet. Resolves false
     * (leaving everything else untouched) if it had already synced by the time this ran — not
     * treated as a chores.error, since a caller racing the sync clock is an expected outcome,
     * not a failure the rest of the UI should react to. */
    undoMarkChoreDone: () => Promise<boolean>;
    dismissUndoableCompletion: () => void;
    deleteChore: (householdId: string, choreId: string) => Promise<void>;
    clearError: () => void;
    updateSyncStatus: (status: Partial<ChoresSlice['chores']['syncStatus']>) => void;
    /** Pushes the queue, then pulls. An action the server refuses outright (isRefusedByServer) is
     * dropped from the queue and reported in failedActions marked `refused`, and the push goes on
     * with the next one; any other failure stops the push, leaving it and what follows queued. */
    syncRemoteFromFirestore: () => Promise<void>;
    forceFullResync: () => Promise<void>;
    /** Pushes the failed actions again, except refused ones, which are no longer queued and stay
     * reported, never pushed, until forgetFailedActions clears them. */
    retryFailedActions: () => Promise<void>;
    forgetFailedActions: () => Promise<void>;
  };
}

export const createChoresSlice: StateCreator<AppStore, [], [], ChoresSlice> = (set, get) => {


  // silent=true skips the loading indicator — used by mutations that do a
  // background refresh after a write so the list stays visible.
  const loadHouseholds = async (silent = false) => {
    const userId = get().auth.user?.uid;
    if (!userId) return;

    if (!silent) set((state) => ({ chores: { ...state.chores, loading: true, error: null } }));
    try {
      const households = await householdService.getHouseholdAggregates(userId);
      // Guard against race condition if user logged out during fetch.
      if (get().auth.user?.uid !== userId) return;

      set((state) => ({ chores: { ...state.chores, households, loading: false } }));
    } catch (error) {
      set((state) => ({
        chores: {
          ...state.chores,
          error: error instanceof Error ? error.message : 'Failed to load households',
          loading: false,
        },
      }));
    }
  };

  // Set when the server refused an action: what it changed is still applied here (a chore ticked
  // off, created, deleted), and only a full fetch brings back the server's version of it, so the
  // next pull that gets to run is a full one.
  let refusedSinceFullPull = false;

  const getPendingActions = async () => {
    try {
      const pendingActions = await syncService.getPendingActions();
      get().chores.updateSyncStatus({ pendingActions });
    } catch (error) {
      console.error('Failed to get pending actions count:', error);
    }
  };

  // Pushes queued local actions, then pulls remote changes. forceFull bypasses the incremental
  // cursor and re-fetches every chore, for use as a manual escape hatch (see forceFullResync).
  const runSyncCycle = async (forceFull: boolean) => {
    const userId = get().auth.user?.uid;
    // In local mode there's no account to sync with: everything stays queued, to be carried into
    // one when the user signs in (localModeService.moveToAccount).
    if (!userId || get().auth.mode !== 'account') return;

    get().chores.updateSyncStatus({ isSyncing: true, lastSyncError: null });
    // A pending action can be pushed by this very cycle, so any offer to undo it — made on the
    // assumption it's still local-only — is stale the moment a sync starts.
    get().chores.dismissUndoableCompletion();
    // Refusals reported earlier are no longer in the queue, so this cycle can't find them again;
    // they stay reported until cleared (forgetFailedActions). Other failures are found afresh.
    const failedActions: OfflineQueueItem[] = get().chores.syncStatus.failedActions.filter(a => a.refused);
    let blocked = false;

    try {
      const pending = await syncService.getPendingActions();
      for (const action of pending) {
        try {
          await syncService.pushAction(action);
        } catch (err) {
          if (isRefusedByServer(err)) {
            // Pushing it again can only be refused again, and would hold up everything after it.
            await syncService.forgetAction(action);
            failedActions.push({ ...action, lastError: refusedMessage(err), refused: true });
            refusedSinceFullPull = true;
            continue;
          }
          failedActions.push({
            ...action,
            lastError: err instanceof Error ? err.message : 'Unknown error',
          });
          blocked = true;
          break;
        }
      }

      // Pulling while something is still queued in front of a failure could show the server's
      // older version over it, so a blocked push skips the pull. A refused action is gone, so
      // the pull goes ahead, in full (see refusedSinceFullPull).
      if (!blocked) {
        const full = forceFull || refusedSinceFullPull;
        await syncService.syncRemoteFromFirestore(userId, { forceFull: full });
        if (full) refusedSinceFullPull = false;
      }

      get().chores.updateSyncStatus({
        isSyncing: false,
        lastSyncTime: new Date(),
        failedActions,
        lastSyncError: describeSyncFailures(failedActions),
      });
    } catch (error) {
      get().chores.updateSyncStatus({
        isSyncing: false,
        lastSyncError: error instanceof Error ? error.message : 'Unknown sync error',
        failedActions,
      });
    }
    await getPendingActions();
    await loadHouseholds(true);
  };

  return {
    chores: {
      households: [],
      loading: false,
      mutating: false,
      error: null,
      undoableCompletion: null,
      syncStatus: {
        isOnline: navigator.onLine,
        isSyncing: false,
        lastSyncTime: null,
        pendingActions: [],
        lastSyncError: null,
        failedActions: [],
      },

      load: async () => {
        if (get().chores.loading) return;

        const userId = get().auth.user?.uid;
        if (!userId) return;

        try {
          set((state) => ({ chores: { ...state.chores, loading: true, error: null } }));

          await Promise.all([loadHouseholds(), getPendingActions()]);

          set((state) => ({ chores: { ...state.chores, loading: false } }));
        } catch (error) {
          set((state) => ({
            chores: {
              ...state.chores,
              loading: false,
              error: error instanceof Error ? error.message : 'Failed to initialize data',
            },
          }));
        }
      },

      reset: async () => {
        refusedSinceFullPull = false;
        set((state) => ({
          chores: {
            ...state.chores,
            users: [],
            households: [],
            loading: false,
            mutating: false,
            error: null,
            undoableCompletion: null,
            syncStatus: {
              isOnline: navigator.onLine,
              isSyncing: false,
              lastSyncTime: null,
              pendingActions: [],
              lastSyncError: null,
              failedActions: [],
            },
          },
        }));
      },

      addChore: async (name, description, schedule, householdId, assignments, labels) => {
        set((state) => ({ chores: { ...state.chores, mutating: true, error: null } }));

        // The chore and its sync action are written together, or neither is (see queueWith), so a
        // failure leaves no chore behind and trying again can't make a second one.
        let chore: Chore;
        try {
          ({ result: chore } = await syncService.queueWith(
            () => householdService.createChore(name, description, schedule, householdId, assignments, labels),
            (created) => ['ChoreCreated', householdId, created.id, {
              name, description, schedule,
              assigned_to: assignments,
              labels: labels ?? [],
              createdAt: created.created_at,
            }],
          ));
        } catch (error) {
          set((state) => ({
            chores: {
              ...state.chores,
              mutating: false,
              error: error instanceof Error ? error.message : 'Failed to add chore',
            },
          }));
          return null;
        }

        set((state) => {
          const households = state.chores.households.map(h => {
            if (h.household.id !== householdId) return h;
            const newChoreAgg: ChoreAggregate = { chore, household: h.household, activities: [] };
            return { ...h, chores: [...h.chores, newChoreAgg] };
          });
          return { chores: { ...state.chores, mutating: false, households } };
        });
        await getPendingActions();

        return chore;
      },

      updateChore: async (householdId, choreId, name, description, schedule, assignments, labels) => {
        set((state) => ({ chores: { ...state.chores, mutating: true, error: null } }));
        try {
          // Changed here and queued together, or neither (see queueWith).
          const { result: updatedChore } = await syncService.queueWith(
            () => householdService.updateChore(choreId, name, description, schedule, assignments, labels),
            () => ['ChoreUpdated', householdId, choreId, {
              name, description, schedule, assigned_to: assignments, labels: labels ?? [],
            }],
          );

          set((state) => {
            const households = state.chores.households.map(h => ({
              ...h,
              chores: h.chores.map(ca =>
                ca.chore.id === choreId ? { ...ca, chore: updatedChore } : ca
              ),
            }));
            return { chores: { ...state.chores, mutating: false, households } };
          });
          await getPendingActions();
        } catch (error) {
          set((state) => ({
            chores: {
              ...state.chores,
              mutating: false,
              error: error instanceof Error ? error.message : 'Failed to update chore',
            },
          }));
        }
      },

      markChoreDone: async (householdId, choreId, userId, completedAt) => {
        set((state) => ({ chores: { ...state.chores, mutating: true, error: null } }));
        try {
          // Done here and queued together, or neither (see queueWith). The action's id is what
          // lets the completion be undone while it's still only here.
          const { result: activity, queueItemId } = await syncService.queueWith(
            () => householdService.markChoreDone(choreId, userId, completedAt),
            (done) => ['ChoreCompleted', householdId, choreId, {
              completedAt: done.completed_at,
              byUser: userId,
            }],
          );

          set((state) => {
            const households = state.chores.households.map(h => ({
              ...h,
              chores: h.chores.map(ca => {
                if (ca.chore.id !== choreId) return ca;
                // A backdated completion may be older than the chore's most recent one;
                // only advance the denormalized cache when this completion is newer.
                const isNewer = !ca.chore.last_completed_at
                  || new Date(activity.completed_at) > new Date(ca.chore.last_completed_at);
                return {
                  ...ca,
                  activities: [...ca.activities, activity],
                  chore: isNewer ? {
                    ...ca.chore,
                    last_completed_at: activity.completed_at,
                    last_completed_by: activity.by_user,
                  } : ca.chore,
                };
              }),
            }));
            return { chores: { ...state.chores, mutating: false, households } };
          });

          const choreName = get().chores.households
            .find(h => h.household.id === householdId)
            ?.chores.find(ca => ca.chore.id === choreId)?.chore.name;

          set((state) => ({
            chores: {
              ...state.chores,
              undoableCompletion: { householdId, choreId, choreName, activityId: activity.id, queueItemId },
            },
          }));

          await getPendingActions();
        } catch (error) {
          set((state) => ({
            chores: {
              ...state.chores,
              mutating: false,
              error: error instanceof Error ? error.message : 'Failed to mark chore done',
            },
          }));
        }
      },

      undoMarkChoreDone: async () => {
        const pending = get().chores.undoableCompletion;
        if (!pending) return false;

        set((state) => ({ chores: { ...state.chores, undoableCompletion: null } }));

        const wasStillPending = await syncService.forgetActionIfPending(pending.queueItemId);
        if (!wasStillPending) return false;

        const updatedChore = await householdService.undoMarkChoreDone(pending.choreId, pending.activityId);

        set((state) => ({
          chores: {
            ...state.chores,
            households: state.chores.households.map(h => {
              if (h.household.id !== pending.householdId) return h;
              return {
                ...h,
                chores: h.chores.map(ca => {
                  if (ca.chore.id !== pending.choreId) return ca;
                  return {
                    ...ca,
                    chore: updatedChore ?? ca.chore,
                    activities: ca.activities.filter(a => a.id !== pending.activityId),
                  };
                }),
              };
            }),
          },
        }));

        await getPendingActions();
        return true;
      },

      dismissUndoableCompletion: () => {
        set((state) => ({ chores: { ...state.chores, undoableCompletion: null } }));
      },

      deleteChore: async (householdId, choreId) => {
        set((state) => ({ chores: { ...state.chores, mutating: true, error: null } }));
        try {
          // Deleted here and queued together, or neither: deleted here only, the next full pull
          // would bring it back from the server.
          await syncService.queueWith(
            () => householdService.deleteChore(choreId),
            () => ['ChoreDeleted', householdId, choreId, {}],
          );

          set((state) => {
            const households = state.chores.households.map(h => ({
              ...h,
              chores: h.chores.filter(ca => ca.chore.id !== choreId),
            }));
            return { chores: { ...state.chores, mutating: false, households } };
          });
          await getPendingActions();
        } catch (error) {
          set((state) => ({
            chores: {
              ...state.chores,
              mutating: false,
              error: error instanceof Error ? error.message : 'Failed to delete chore',
            },
          }));
        }
      },

      addHousehold: async (name: string, ownerId: string, ownerDisplayName: string) => {
        set((state) => ({ chores: { ...state.chores, mutating: true, error: null } }));
        try {
          // The household and its sync action are written together, or neither is (see queueWith).
          const { result: household } = await syncService.queueWith(
            () => householdService.createHousehold(name, ownerId, ownerDisplayName),
            (created) => ['HouseholdCreated', created.id, undefined, {
              name: created.name,
              created_at: created.created_at,
              owner_id: created.owner_id,
              owner_display_name: created.owner_display_name,
              member_ids: created.member_ids,
              member_display_names: created.member_display_names,
            }],
          );

          const owner: HouseholdMember = { id: ownerId, name: ownerDisplayName };
          const newHouseholdAgg: HouseholdAggregate = { household, members: [owner], chores: [] };
          set((state) => ({
            chores: {
              ...state.chores,
              mutating: false,
              households: [...state.chores.households, newHouseholdAgg],
            },
          }));
          await getPendingActions();
        } catch (error) {
          set((state) => ({
            chores: {
              ...state.chores,
              mutating: false,
              error: error instanceof Error ? error.message : 'Failed to add household',
            },
          }));
        }
      },

      deleteHousehold: async (householdId: string) => {
        set((state) => ({ chores: { ...state.chores, mutating: true, error: null } }));
        try {
          // Deleted here and queued together, or neither: deleted here only, the next pull would
          // bring it back from the server.
          await syncService.queueWith(
            () => householdService.deleteHousehold(householdId),
            () => ['HouseholdDeleted', householdId, undefined, {}],
          );

          set((state) => ({
            chores: {
              ...state.chores,
              mutating: false,
              households: state.chores.households.filter(h => h.household.id !== householdId),
            },
          }));
          await getPendingActions();
        } catch (error) {
          set((state) => ({
            chores: {
              ...state.chores,
              mutating: false,
              error: error instanceof Error ? error.message : 'Failed to delete household',
            },
          }));
        }
      },

      removeMember: async (householdId: string, memberId: string) => {
        await membershipService.removeMember(householdId, memberId);
        // The next pull would bring the same change; applied here, the owner sees it now.
        await householdService.removeMember(householdId, memberId);
        await loadHouseholds(true);
      },

      setChild: async (householdId: string, memberId: string, isChild: boolean) => {
        await membershipService.setChild(householdId, memberId, isChild);
        // As with removeMember: the next pull would bring it, applied here the owner sees it now.
        await householdService.setChild(householdId, memberId, isChild);
        await loadHouseholds(true);
      },

      clearError: () => {
        set((state) => ({ chores: { ...state.chores, error: null } }));
      },

      updateSyncStatus: (status) => {
        set((state) => ({
          chores: {
            ...state.chores,
            syncStatus: { ...state.chores.syncStatus, ...status },
          },
        }));
      },

      syncRemoteFromFirestore: async () => {
        await runSyncCycle(false);
      },

      // Ignores the incremental sync cursor and re-fetches every chore from Firestore.
      // Escape hatch for the case where a chore's own updated_at cursor got stuck (e.g. legacy
      // data written before updated_at became a serverTimestamp) and never surfaces again on its
      // own — a normal sync would keep silently skipping it forever.
      forceFullResync: async () => {
        await runSyncCycle(true);
      },

      retryFailedActions: async () => {
        const failedActions = get().chores.syncStatus.failedActions;
        const userId = get().auth.user?.uid;
        if (!userId || get().auth.mode !== 'account') return;
        // A refused action is already out of the queue: pushing it again would only be refused
        // again, so it isn't retried, just kept in the report.
        const retryable = failedActions.filter(a => !a.refused);
        if (retryable.length === 0) return;

        get().chores.updateSyncStatus({ isSyncing: true, lastSyncError: null });
        const stillFailed: OfflineQueueItem[] = failedActions.filter(a => a.refused);

        for (const action of retryable) {
          try {
            await syncService.pushAction(action);
          } catch (err) {
            if (isRefusedByServer(err)) {
              await syncService.forgetAction(action);
              stillFailed.push({ ...action, lastError: refusedMessage(err), refused: true });
              refusedSinceFullPull = true;
              continue;
            }
            stillFailed.push({
              ...action,
              lastError: err instanceof Error ? err.message : 'Unknown error',
            });
          }
        }

        // Bring back the server's version of what was refused (see refusedSinceFullPull), unless
        // anything is still queued (a failure, or what was behind it), which a pull mustn't
        // overtake (see runSyncCycle). Not getting it now is no failure of the retry: the next
        // sync's pull is a full one.
        let pulled = false;
        try {
          if (refusedSinceFullPull && (await syncService.getPendingActions()).length === 0) {
            await syncService.syncRemoteFromFirestore(userId, { forceFull: true });
            refusedSinceFullPull = false;
            pulled = true;
          }
        } catch (error) {
          console.error('Failed to pull after a refused action:', error);
        }

        const anyRetryableLeft = stillFailed.some(a => !a.refused);
        get().chores.updateSyncStatus({
          isSyncing: false,
          failedActions: stillFailed,
          lastSyncError: describeSyncFailures(stillFailed),
          lastSyncTime: !anyRetryableLeft
            ? new Date()
            : get().chores.syncStatus.lastSyncTime,
        });
        await getPendingActions();
        if (pulled) await loadHouseholds(true);
      },

      forgetFailedActions: async () => {
        const failedActions = get().chores.syncStatus.failedActions;
        for (const action of failedActions) {
          // A refused one is already out of the queue; clearing the report is all that's left.
          if (!action.refused) await syncService.forgetAction(action);
        }
        get().chores.updateSyncStatus({ failedActions: [], lastSyncError: null });
        await getPendingActions();
      },
    },
  };
};
